import { useState } from "react";
import { Search, Loader2, Package, Truck, Mail } from "lucide-react";
import { toast } from "sonner";
import LegalLayout from "@/components/LegalLayout";
import { api } from "@/lib/book";

export default function TrackOrder() {
  const [email, setEmail] = useState("");
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter the email you used at checkout");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/lookup", {
        email: email.trim(),
        order_id: orderId.trim() || null,
      });
      setOrders(data.orders || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Lookup failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LegalLayout
      eyebrow="Order tracking"
      title="Track your order."
      lastUpdated={null}
      testid="page-track"
    >
      <p className="text-lg">
        Enter the email you used at checkout. If you have your order number (starts with <span className="font-mono-plex">pi_</span> or <span className="font-mono-plex">cs_</span>), include it too — it'll find your order faster.
      </p>

      <form onSubmit={submit} className="mt-8 bg-white border border-black/10 p-7 space-y-4" data-testid="track-form">
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full h-12 px-3 bg-white border border-black/15 focus:border-[#0A0A0A] focus:outline-none text-sm"
            data-testid="track-email"
            placeholder="you@email.com"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">Order number (optional)</label>
          <input
            type="text"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className="mt-2 w-full h-12 px-3 bg-white border border-black/15 focus:border-[#0A0A0A] focus:outline-none text-sm font-mono-plex"
            data-testid="track-order-id"
            placeholder="pi_3T... or cs_live_..."
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-7 py-4 font-medium hover:bg-[#D92D20] transition-colors disabled:opacity-60"
          data-testid="track-submit"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          Look up my order
        </button>
      </form>

      {orders !== null && (
        <div className="mt-10" data-testid="track-results">
          {orders.length === 0 ? (
            <div className="bg-white border border-black/10 p-7">
              <h2 className="font-display font-bold text-xl tracking-tight">No orders found.</h2>
              <p className="mt-3 text-[#525252]">
                Double-check the email used at checkout. If it's still not appearing, email <a href="mailto:orders@12lawsbook.com" className="underline text-[#D92D20]">orders@12lawsbook.com</a> and we'll find it for you.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#525252]">
                {orders.length} order{orders.length === 1 ? "" : "s"} found
              </div>
              {orders.map((o) => (
                <article key={o.id} className="bg-white border border-black/10 p-6 sm:p-7" data-testid={`track-order-${o.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252]">Order</div>
                      <div className="font-mono-plex text-sm mt-1 break-all">{o.session_id}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252]">Total paid</div>
                      <div className="font-display font-bold text-2xl tracking-tight">${o.amount_total?.toFixed(2)}</div>
                    </div>
                  </div>

                  <dl className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4 text-sm">
                    <Cell label="Status" value={<span className="inline-flex items-center gap-1 text-[#D92D20]"><Package className="w-3 h-3" /> Paid · Preparing to ship</span>} />
                    <Cell label="Qty" value={o.quantity} />
                    <Cell label="Date" value={(o.created_at || "").slice(0, 10)} />
                    <Cell label="Shipping" value={o.shipping_service || "—"} />
                    <Cell
                      label="Ship to"
                      value={`${o.shipping_address?.city || ""}, ${o.shipping_address?.state || o.shipping_address?.country || ""}`}
                    />
                    <Cell label="Email" value={o.customer_email} />
                  </dl>

                  <div className="mt-6 pt-5 border-t border-black/10 text-xs text-[#525252] flex items-start gap-2">
                    <Truck className="w-4 h-4 text-[#D92D20] shrink-0 mt-0.5" />
                    <span>
                      Tracking number will be emailed once your order ships. Most orders ship within 2 business days. Need help? <a href="/contact" className="underline">Contact us</a>.
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      )}
    </LegalLayout>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252]">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}
